/* eslint-disable no-empty */
import { cmpCookie as consent } from "@foundation/cmp-client";
import cookie from "./helper/cookie";
import getSessionInfo from "./helper/getSessionInfo";
import { BRIDGE_KEYS } from "../../constants/bridgeKeys";
import hasExpired from "./helper/hasExpired";

// Bridge-Web can be fully synchronous and be functionally equivalent
// We maintain each as a promise becase it keeps it in sync and 1:1 with Bridge-Mobile
// Bridge-Mobile does not have synchronous get/set interactions with its data store
export default class Bridge {
    constructor() {
        window.addEventListener("beforeunload", () => {
            try {
                window.sessionStorage.removeItem(BRIDGE_KEYS.CONTENT_METADATA_KEY);
                window.sessionStorage.removeItem(BRIDGE_KEYS.MOST_RECENT_RESOURCE);
            } catch (err) {
                // Do nothing
            }
        });
    }

    setItem(key, val, sessionStorage = false, expirationDate = null) {
        return new Promise(function (resolve, reject) {
            try {
                sessionStorage
                    ? window.sessionStorage.setItem(key, JSON.stringify(val))
                    : window.localStorage.setItem(key, JSON.stringify(val));

                if (expirationDate !== null) {
                    sessionStorage
                        ? window.sessionStorage.setItem(
                            key + BRIDGE_KEYS.BRIDGE_EXPIRATION_KEY_SUFFIX, JSON.stringify(expirationDate))
                        : window.localStorage.setItem(
                            key + BRIDGE_KEYS.BRIDGE_EXPIRATION_KEY_SUFFIX, JSON.stringify(expirationDate));
                }

                resolve();
            } catch (err) {
                reject(err);
            }
        });
    }

    getItem(key, sessionStorage = false, checkExpiration = false) {
        return new Promise(function (resolve) {
            try {
                if (checkExpiration) {
                    const expirationDate = sessionStorage
                        ? JSON.parse(window.sessionStorage.getItem(key + BRIDGE_KEYS.BRIDGE_EXPIRATION_KEY_SUFFIX))
                        : JSON.parse(window.localStorage.getItem(key + BRIDGE_KEYS.BRIDGE_EXPIRATION_KEY_SUFFIX));

                    if (hasExpired(expirationDate)) {
                        return resolve(null);
                    }
                }

                sessionStorage
                    ? resolve(JSON.parse(window.sessionStorage.getItem(key)))
                    : resolve(JSON.parse(window.localStorage.getItem(key)));
            } catch (err) {
                resolve(null);
            }
        });
    }

    getReferrer() {
        let referrer = "";

        try {
            referrer = window?.__bloomberg__?.referrer ?? document.referrer;
        } catch (e) {}

        return Promise.resolve(referrer);
    }

    /**
     * Fetchs the url from window.location.href
     *
     * @returns {Promise<String>} - the current Url
     **/
    getUrl() {
        let url = "";

        try {
            url = window.location.href;
        } catch (e) {}

        return Promise.resolve(url);
    }

    /**
     *
     * @param {String} keyToMatch - optional string that if present represents the
     *                   values of the query parameters to return
     * @returns {Promise<URLSearchParams>} - all query parameters
     *                    or only the subset that match the paramter keyToMatch (e.g., 'utm_.*')
     *                    (uses a case insensitive match)
     **/
    getQueryParams(keyToMatch = undefined) {
        let subsetQueryParams = new URLSearchParams();

        try {
            const params = new URLSearchParams(window.location.search);

            if (!keyToMatch) {
                subsetQueryParams = params;
            } else {
                const regularExp = new RegExp(keyToMatch, "i");

                for (const [key, value] of params.entries()) {
                    if (key.match(regularExp)) {
                        subsetQueryParams.append(key, value);
                    }
                }
            }
        } catch (e) {}

        return Promise.resolve(subsetQueryParams);
    }

    getCurrentResource() {
        return this.getItem(BRIDGE_KEYS.CONTENT_METADATA_KEY, true).then((contentMetadata) => {
            const { id, contentType } = contentMetadata ?? {};

            if (id && contentType) {
                return {
                    id,
                    contentType,
                };
            } else {
                return {};
            }
        });
    }

    needsPersonalizationRefresh() {
        let needsRefresh = true;

        return this.getCurrentResource()
            .then(({ id, contentType }) => {
                const currentResource = `${ contentType }|${ id }`;

                return this.getItem(BRIDGE_KEYS.MOST_RECENT_RESOURCE, true)
                    .then((mostRecentResource) => {
                        needsRefresh = (contentType === undefined && id === undefined) || mostRecentResource !== currentResource;

                        return this.setItem(BRIDGE_KEYS.MOST_RECENT_RESOURCE, currentResource, true);
                    })
                    .then(() => {
                        return needsRefresh;
                    });
            });
    }

    getPersonalizationAllowed() {
        return Promise.resolve(consent.padAllowed());
    }

    // Web relies on the cmp-client library for user consent for personalization; we don't set it here
    // Bridge-Mobile needs and impelements this however, so we need a no-op placeholder here
    setPersonalizationAllowed(personalizationAllowed) {
        return Promise.resolve(undefined);
    }

    getSessionInfo() {
        return getSessionInfo();
    }

    injectIFrame(parentDOMNode, params, baseUrl = "") {
        if ((parentDOMNode && parentDOMNode.getElementsByTagName("iframe").length > 0) || baseUrl === "") {
            parentDOMNode = null;

            return false;
        }

        let temporaryIFrame = document.createElement("iframe");

        temporaryIFrame.setAttribute("src", baseUrl);
        temporaryIFrame.setAttribute("style", "width: 100%; height: " + params.height);
        parentDOMNode.appendChild(temporaryIFrame);
        parentDOMNode = null;
        temporaryIFrame = null;

        return true;
    }

    getCookie(key) {
        const urlDecodedCookie = decodeURIComponent(cookie.get(document, key));

        try {
            return JSON.parse(urlDecodedCookie);
        } catch {
            return urlDecodedCookie;
        }
    }
}
